import { whenIdle } from './whenIdle'

/**
 * Warm the Fingerprint Pro agent on idle time.
 *
 * #819 moved the agent's `start()` — ~620 ms of main-thread entropy collection
 * on a mid-tier phone — off page load, so every surface that can later reach
 * `identify()` warms it in advance instead. Surfaces driven by a user action
 * (the login modal opening, the OAuth callback page) can call
 * `$fingerprint.prepare()` directly: the user is reading or waiting, and the
 * main thread is theirs to spend.
 *
 * This helper is for the other kind — warm-ups that hang off a PAGE-LOAD path,
 * where calling `prepare()` inline would put the 620 ms straight back into the
 * window the ticket exists to clear, competing with hydration and the Pixi boot.
 * `requestIdleCallback` is the right primitive for "after first paint, when the
 * main thread is free"; Safari < 16.4 falls back to a macrotask, same as
 * `useWarmupPing`.
 *
 * Bills nothing — only `identify()` charges a Pro event — and is a no-op when
 * some other surface already warmed the agent (`prepare()` is memoised).
 */
export function warmFingerprintWhenIdle(
  fingerprint?: { prepare?: () => unknown } | null,
): void {
  if (typeof window === 'undefined') return
  if (!fingerprint?.prepare) return

  const fire = (): void => {
    try {
      fingerprint.prepare?.()
    } catch {
      // Fire-and-forget: a warm-up failure must never surface to the app. The
      // real call path (identify()) has its own fallback to `fb_<uuid>`.
    }
  }

  whenIdle(fire)
}
